import axios from 'axios';
import { 
  ApiResponse, 
  User, 
  LoginRequest, 
  RegisterRequest, 
  ChangePasswordRequest, 
  Attendance, 
  Leave, 
  CompanyResponse,
  JoiningLetterGenerateRequest,
  JoiningLetterSendRequest,
  RelievingLetterGenerateRequest,
  RelievingLetterSendRequest,
  LetterEmailSendResult,
} from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

console.log('API Base URL:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only clear auth data, don't force redirect during login attempts
      const isLoginAttempt = error.config?.url?.includes('/auth/login');
      
      if (!isLoginAttempt) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Use router navigation instead of hard redirect
        if (typeof window !== 'undefined' && window.location.pathname !== '/auth/login') {
          window.location.href = '/auth/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: async (credentials: LoginRequest): Promise<ApiResponse<any>> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  register: async (userData: RegisterRequest): Promise<ApiResponse<User>> => {
    const response = await api.post('/employees/register', userData);
    return response.data;
  },

  logout: async (): Promise<ApiResponse<null>> => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  me: async (): Promise<ApiResponse<User>> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  changePassword: async (passwords: ChangePasswordRequest): Promise<ApiResponse<null>> => {
    const response = await api.put('/auth/change-password', passwords);
    return response.data;
  },
};

// Employee API
export const employeeApi = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    department?: string;
    search?: string;
  }): Promise<ApiResponse<User[]>> => {
    const response = await api.get('/employees', { params });
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<User>> => {
    const response = await api.get(`/employees/${id}`);
    return response.data;
  },

  create: async (userData: RegisterRequest): Promise<ApiResponse<User>> => {
    const response = await api.post('/employees/register', userData);
    return response.data;
  },

  update: async (id: string, userData: Partial<User>): Promise<ApiResponse<User>> => {
    const response = await api.put(`/employees/${id}`, userData);
    return response.data;
  },

  deactivate: async (id: string): Promise<ApiResponse<User>> => {
    const response = await api.delete(`/employees/${id}`);
    return response.data;
  },

  activate: async (id: string): Promise<ApiResponse<User>> => {
    const response = await api.put(`/employees/${id}/activate`);
    return response.data;
  },

  toggleOfferLetter: async (id: string, isOfferLetter: boolean): Promise<ApiResponse<User>> => {
    const response = await api.put(`/employees/${id}/toggle-offer-letter`, { isOfferLetter });
    return response.data;
  },
};

// Attendance API
export const attendanceApi = {
  getMyAttendance: async (params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<Attendance[]>> => {
    const response = await api.get('/attendance/my-attendance', { params });
    return response.data;
  },

  getAllAttendance: async (params?: {
    page?: number;
    limit?: number;
    employeeId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<Attendance[]>> => {
    const response = await api.get('/attendance', { params });
    return response.data;
  },

  checkIn: async (latitude: number, longitude: number, address: string): Promise<ApiResponse<Attendance>> => {
   
   console.log({ latitude, longitude, address },"location in api.ts==")
    const response = await api.post('/attendance/check-in', { latitude, longitude, address });
    return response.data;
  },

  checkOut: async (latitude: number, longitude: number, address: string): Promise<ApiResponse<Attendance>> => {
    const response = await api.put('/attendance/check-out', { latitude, longitude, address });
    return response.data;
  },

  getTodayAttendance: async (): Promise<ApiResponse<Attendance>> => {
    const response = await api.get('/attendance/today-status');
    return response.data;
  },
};

// Leave API
export const leaveApi = {
  getMyLeaves: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<ApiResponse<Leave[]>> => {
    const response = await api.get('/leaves/my-leaves', { params });
    return response.data;
  },

  getPendingLeaves: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<Leave[]>> => {
    const response = await api.get('/leaves/pending', { params });
    return response.data;
  },

  apply: async (leaveData: {
    leaveType: string;
    startDate: string;
    endDate: string;
    reason: string;
    isHalfDay: boolean;
    halfDayPeriod?: string;
  }): Promise<ApiResponse<Leave>> => {
    const response = await api.post('/leaves/apply', leaveData);
    return response.data;
  },

  approve: async (id: string): Promise<ApiResponse<Leave>> => {
    const response = await api.put(`/leaves/${id}/approve`);
    return response.data;
  },

  reject: async (id: string, rejectionReason: string): Promise<ApiResponse<Leave>> => {
    const response = await api.put(`/leaves/${id}/reject`, { rejectionReason });
    return response.data;
  },
};

// Offer Letter API
export const offerLetterApi = {
  download: async (employeeId?: string): Promise<Blob> => {
    const response = await api.get(`/offer-letter/generate/${employeeId ? `${employeeId}` : ''}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  generateJoiningLetter: async (
    employeeId: string,
    data: JoiningLetterGenerateRequest,
  ): Promise<Blob> => {
    const response = await api.post(
      `/offer-letter/joining-letter/generate/${employeeId}`,
      data,
      { responseType: 'blob' },
    );
    return response.data;
  },

  generateRelievingLetter: async (
    employeeId: string,
    data: RelievingLetterGenerateRequest,
  ): Promise<Blob> => {
    const response = await api.post(
      `/offer-letter/relieving-letter/generate/${employeeId}`,
      data,
      { responseType: 'blob' },
    );
    return response.data;
  },

  sendJoiningLetter: async (
    employeeId: string,
    data: JoiningLetterSendRequest,
  ): Promise<ApiResponse<LetterEmailSendResult>> => {
    const response = await api.post(
      `/offer-letter/joining-letter/send/${employeeId}`,
      data,
    );
    return response.data;
  },

  sendRelievingLetter: async (
    employeeId: string,
    data: RelievingLetterSendRequest,
  ): Promise<ApiResponse<LetterEmailSendResult>> => {
    const response = await api.post(
      `/offer-letter/relieving-letter/send/${employeeId}`,
      data,
    );
    return response.data;
  },
};

// Company API - Single company system matching backend model
export const companyApi = {
  // Get the company details
  getCompany: async (): Promise<CompanyResponse> => {
    const response = await api.get('/company');
    return response.data;
  },

  // Create or update company (matches your backend PUT route)
  saveCompany: async (companyData: any): Promise<CompanyResponse> => {
    console.log(companyData,"=--=-")
    const response = await api.put('/company', companyData);
    return response.data;
  },

  // Update company details (alias for saveCompany)
  updateCompany: async (companyData: any): Promise<CompanyResponse> => {
     console.log(companyData,"=--=-888")
    const response = await api.put('/company', companyData);
    return response.data;
  },
};

// Change Password API (standalone export)
export const changePassword = async (passwords: ChangePasswordRequest): Promise<ApiResponse<null>> => {
  const response = await api.put('/auth/change-password', passwords);
  return response.data;
};

export default api;